import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { environment } from '@mortgage-pos/ui/env'
import { redirectTo } from '@mortgage-pos/utils'
import { useStoreActions, useStoreState } from '@mortgage-pos/ui/store'
import { useVariation } from '@mortgage-pos/ui/services/featureFlags'
import { FEATURE_FLAG_TYPES } from '@mortgage-pos/data'
import {
  Routes,
  LoanPurpose,
  Status,
  PropertyUse,
  PLAID_FLOW_TASK_NAME,
  QuestionSetSlug,
} from '@mortgage-pos/types'

export const useQuestionnaireFinished = () => {
  const navigate = useNavigate()

  const isPhoneFlowEnabled = useVariation(FEATURE_FLAG_TYPES.PHONE_FLOW_ENABLED)
  const skipBorrowerPortal = useVariation(
    FEATURE_FLAG_TYPES.SKIP_BORROWER_PORTAL
  )
  const enableSimpleV1 = useVariation(FEATURE_FLAG_TYPES.ENABLE_SIMPLE_V1)

  const { mergedAnswers } = useStoreState((state) => state.answers)
  const { selectedRate } = useStoreState((state) => state.rates)
  const {
    isSimple,
    finished,
    statusesMap,
    isSplitFlow,
    isPrePricingFlow,
    isPhoneFlow,
    isFullFlow,
    isAmazonBenefitsFlow,
    isPlaidEnabled,
    slug,
  } = useStoreState((state) => state.questionnaire)

  const { lockWipLockForm } = useStoreActions((state) => state.rates)
  const { completeAllSections } = useStoreActions((state) => state.pageSections)
  const { updateStatus } = useStoreActions((state) => state.application)
  const { addTaskByName } = useStoreActions((state) => state.task)

  // Helper to determine if we're in the apply-purchase flow for simple-v1
  const isApplyPurchaseInSimpleV1 =
    enableSimpleV1 &&
    slug === QuestionSetSlug.ApplyPurchase &&
    mergedAnswers.loanPurpose === LoanPurpose.Purchase

  useEffect(() => {
    if (!finished) return

    async function handleSubmit() {
      completeAllSections()

      if (
        isPlaidEnabled &&
        (mergedAnswers.loanPurpose !== LoanPurpose.Refinance ||
          mergedAnswers.rollInFees === 'No')
      ) {
        await addTaskByName({ taskName: PLAID_FLOW_TASK_NAME })
      }

      // For simple-v1 flow, run Desktop Underwriter before redirecting
      if (isApplyPurchaseInSimpleV1) {
        // Navigate back to Simple V1 Page for AUS run
        navigate(
          `/${Routes.APP_1003}?question-set=${QuestionSetSlug.SimpleV1Results}`
        )
        window.location.reload()
        return
      }

      if (isAmazonBenefitsFlow) {
        return navigate(`/${Routes.APP_1003}/completed`)
      }

      // Amex Demo only
      if (mergedAnswers.isAmexDemo) {
        return navigate(`/${Routes.APP_1003}/prequal`)
      }

      if (skipBorrowerPortal) {
        return mergedAnswers.loanPurpose === LoanPurpose.Purchase
          ? navigate(`/${Routes.APP_1003}/prequal`)
          : navigate(`/${Routes.APP_1003}/no-rates`)
      }

      if (isPrePricingFlow) {
        await updateStatus(statusesMap.completed)

        if (
          enableSimpleV1 &&
          mergedAnswers.loanPurpose === LoanPurpose.Purchase &&
          mergedAnswers.purchaseStage === 'preapproval'
        ) {
          navigate(
            `/${Routes.APP_1003}?question-set=${QuestionSetSlug.SimpleV1}`
          )
          window.location.reload()
          return
        }

        if (selectedRate && isSimple) {
          try {
            return navigate(
              `/${Routes.APP_1003}/summary${isSimple ? '?simple=true' : ''}`
            )
          } catch (error) {
            console.error('Failed to add task:', error)
          }
        }

        if (mergedAnswers.loanPurpose === LoanPurpose.Purchase) {
          if (mergedAnswers.purchaseStage === 'contract' && selectedRate) {
            navigate(`/${Routes.APP_1003}/summary`)
          } else {
            // Do not run prequal for Investment properties
            if (mergedAnswers.propertyUse !== PropertyUse.InvestmentOrRental) {
              navigate(`/${Routes.APP_1003}/prequal`)
            } else {
              navigate(`/${Routes.APP_1003}/completed`)
            }
          }

          return
        }

        if (selectedRate && mergedAnswers.isNtlQualified) {
          navigate(`/${Routes.APP_1003}/summary`)
          return
        }

        navigate(`/${Routes.APP_1003}/completed`)
        return
      }

      if (isPhoneFlow && isPhoneFlowEnabled) {
        await updateStatus(statusesMap.completed)
        await updateStatus(Status.AlfieCompleted)
        navigate(`/${Routes.APP_1003}/completed`)
        return
      }

      if (environment.borrowerPortal.redirectEnabled && !skipBorrowerPortal) {
        if (isSplitFlow) {
          await updateStatus(statusesMap.completed)
          await updateStatus(Status.AlfieCompleted)
          await lockWipLockForm()
          navigate(`/${Routes.APP_1003}/completed`)
          return
        }

        if (isFullFlow) {
          await updateStatus(statusesMap.completed)
          await updateStatus(Status.AlfieCompleted)
        }

        return redirectTo(environment.borrowerPortal.uiBaseUrl)
      }

      navigate(`/${Routes.APP_1003}/success`)
    }

    handleSubmit()
  }, [
    navigate,
    finished,
    selectedRate,
    isApplyPurchaseInSimpleV1,
    enableSimpleV1,
  ])
}

export default useQuestionnaireFinished
